export interface OutlineItem {
  level: number;
  text: string;
  id: string;
  children: OutlineItem[];
}

export function generateMarkdownHeadingId(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\u4e00-\u9fa5]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function createUniqueMarkdownHeadingId(
  text: string,
  headingCount: Record<string, number>,
): string {
  const baseId = generateMarkdownHeadingId(text);
  if (!baseId) return '';

  if (headingCount[baseId] === undefined) {
    headingCount[baseId] = 0;
    return baseId;
  }

  headingCount[baseId]++;
  return `${baseId}-${headingCount[baseId]}`;
}

/**
 * 从 Markdown 文本中提取大纲
 * @param markdown Markdown 文本
 * @returns 大纲项目数组
 */
export function extractOutlineFromMarkdown(markdown: string): OutlineItem[] {
  const lines = markdown.split('\n');
  const headers: { level: number; text: string; id: string }[] = [];

  // 用于跟踪相同标题的计数
  const headingCount: Record<string, number> = {};

  // 提取所有带数字编号的标题
  for (const line of lines) {
    const headerMatch = line.match(/^(#{1,6})\s+(\d+[.\d]*)\s+(.+)$/);
    if (headerMatch) {
      const level = headerMatch[1].length;
      const number = headerMatch[2];
      const title = headerMatch[3].trim();
      const text = number + ' ' + title;

      const id = createUniqueMarkdownHeadingId(text, headingCount);

      headers.push({ level, text, id });
    }
  }

  // 构建大纲树结构
  const root: OutlineItem[] = [];
  const stack: OutlineItem[] = [];

  for (const header of headers) {
    const item: OutlineItem = {
      level: header.level,
      text: header.text,
      id: header.id,
      children: [],
    };

    // 找到合适的父节点
    while (stack.length > 0 && stack[stack.length - 1].level >= header.level) {
      stack.pop();
    }

    if (stack.length === 0) {
      root.push(item);
    } else {
      stack[stack.length - 1].children.push(item);
    }

    stack.push(item);
  }

  return root;
}

export function flattenOutlineItems(outline: OutlineItem[]): OutlineItem[] {
  return outline.flatMap((item) => [
    item,
    ...flattenOutlineItems(item.children),
  ]);
}

export function toMarkdownCopyText(
  content: string,
  references: Array<{ link?: string | null }>,
): string {
  return content.replace(/\[\[(\d+)]]/g, (_match, n: string) => {
    const index = Number(n) - 1;
    const reference = references[index];
    if (!reference) return '';
    return `[${n}](${reference.link ?? ''})`;
  });
}

/**
 * 为HTML内容中的标题添加锚点ID
 * @param html HTML内容
 * @returns 添加了锚点ID的HTML内容
 */
export function addHeaderAnchors(html: string): string {
  const headingCount: Record<string, number> = {};

  return html.replace(
    /<h([1-6])>(\d+[.\d]*\s+.+?)<\/h\1>/g,
    (_, level, content) => {
      const text = content.trim();
      const id = createUniqueMarkdownHeadingId(text, headingCount);
      return `<h${level} id="${id}">${content}</h${level}>`;
    },
  );
}
